import React from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import { barFlex, buildTicks, fullLabel, tickAlign } from './chartAxis.js';

const CHART_H = 84;

/**
 * Stacked bar chart: one bar per date bucket, split into coloured segments.
 *
 * `dates` is the shared domain; `groups` are `{ key, label, color, series: [count per date] }`
 * (e.g. SMS split by notification type). Empty groups are dropped from the legend. Reuses the
 * shared axis so it lines up with the MiniBars/GroupedBars on the same page.
 */
export default function StackedBars({ dates, groups, bucket = 'day', label }) {
  if (!dates || dates.length === 0 || !groups || groups.length === 0) return null;
  const active = groups.filter((g) => (g.series || []).some((v) => v > 0));
  if (active.length === 0) return null;

  const totals = dates.map((_, i) => active.reduce((s, g) => s + (g.series[i] || 0), 0));
  const max = Math.max(1, ...totals);
  const ticks = buildTicks(dates, bucket);
  const flex = barFlex(bucket);

  return (
    <Box sx={{ mt: 1.5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>{label}</Typography>
        <Typography variant="caption" color="text.disabled">max {max}</Typography>
      </Box>

      {/* Legend — only shown when there's more than one segment type */}
      {active.length > 1 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25, mt: 0.5, mb: 0.25 }}>
          {active.map((g) => (
            <Box key={g.key} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: g.color, flexShrink: 0 }} />
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>{g.label}</Typography>
            </Box>
          ))}
        </Box>
      )}

      <Box sx={{ display: 'flex', mt: 0.5, width: '100%' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
          height: CHART_H, pr: 0.75, flexShrink: 0 }}>
          <Typography variant="caption" color="text.disabled" sx={{ fontSize: 10, lineHeight: 1 }}>{max}</Typography>
          <Typography variant="caption" color="text.disabled" sx={{ fontSize: 10, lineHeight: 1 }}>0</Typography>
        </Box>

        <Box sx={{ overflowX: 'auto', pb: 0.5, flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '3px', height: CHART_H,
            borderBottom: '1px solid', borderColor: 'divider' }}>
            {dates.map((date, i) => {
              const total = totals[i];
              const parts = active.map((g) => ({ g, v: g.series[i] || 0 })).filter((p) => p.v > 0);
              const title = `${fullLabel(date, bucket)}: ${total}`
                + (parts.length ? ` — ${parts.map((p) => `${p.g.label} ${p.v}`).join(', ')}` : '');
              return (
                <Tooltip key={date} title={title} arrow>
                  <Box sx={{
                    ...flex, height: `${total ? Math.max(2, (total / max) * (CHART_H - 4)) : 0}px`,
                    display: 'flex', flexDirection: 'column-reverse',
                    borderRadius: '2px 2px 0 0', overflow: 'hidden', cursor: 'default',
                  }}>
                    {parts.map((p) => (
                      <Box key={p.g.key} sx={{ height: `${(p.v / total) * 100}%`, bgcolor: p.g.color }} />
                    ))}
                  </Box>
                </Tooltip>
              );
            })}
          </Box>
          <Box sx={{ display: 'flex', gap: '3px', mt: 0.5 }}>
            {dates.map((date, i) => (
              <Box key={date} sx={{ ...flex, display: 'flex', flexDirection: 'column',
                alignItems: tickAlign(i, dates.length), lineHeight: 1.15 }}>
                {(ticks[i] || []).map((line) => (
                  <Typography key={line} variant="caption" color="text.disabled"
                    sx={{ fontSize: 9, lineHeight: 1.15, whiteSpace: 'nowrap' }}>{line}</Typography>
                ))}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
